// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import { executePolling, REQUEST_STATUS_COMPLETED } from '../../../../../share/util/api/polling';
import { executeSyncRequest } from '../../../../../share/util/api/syncRequest';
import { apiErrorDefaultAction } from '../../../../../share/util/api/errorAction';
import { OBD_DIAGNOSE_FREQUENCY } from '../../../../../constants/usageCountTypes';
import { createObdDiagnoseFrequencyItems } from '../../../../../util/obd/diag';
import { pushFunctionList } from '../../../../../router/obd';
import apiService from '../../../../../share/util/api/service';
import alert from '../../../../../share/alert';
import cstmTable from '../../../../../share/components/vue/PaginationTable';
import diagnoseResource from '../../../../../share/util/diagnose';
import loading from '../../../../../share/components/vue/ProcessingIcon';
import util from '../../../../../share/util/utils';
import memoPage from '../../../../vue/MemoPage';
import eventLogUtil from '../../../../../share/util/eventLogUtil';
import { getMeasuredDataName, getCsvFileName } from '../../../../../util/fileExport';

export default {
  name: 'diagnoseFrequency',
  data() {
    return {
      measureId: undefined,
      processing: false,
      getDiagnoseFrequencyResponse: undefined,
      diagnoseFrequencyResultItems: [],
      diagnoseFrequencyResultColumns: [],
      isMemoDialogLoading: false,
      modalMemo: false,
      isExecutedFromStartScreen: false,
    };
  },

  props: {
    obdFunctionLabelKey: { type: String, required: false, default: 'fixed_resource.obd_menu' },
    functionNameKey: { type: String, required: false, default: 'fixed_resource.obd_menu_jumpr' },
    destinationAPI: { type: String, required: false, default: apiService.OBD },
  },

  components: {
    'processing-view': loading,
    'cstm-table': cstmTable,
    'memo-page': memoPage,
  },

  computed: {
    ...mapState({
      language: state => state.systems.language,
      vehicleInfo: state => state.vehicle.vehicleInfo,
      netAppId: state => state.netAppConnector.pairingAppId,
      projectId: state => state.vehicle.vehicleInfo ? state.vehicle.vehicleInfo.vehicle.project.id : null,
      obdDataId: state => state.obd.obdDataId,
    }),

    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断/OBDシステムのうちどの機能なのか判別する
    operationScreen() {
      return diagnoseResource.operationFrom(this.$router.currentRoute.value.name);
    },
  },

  watch: {
    language: {
      immediate: true,
      handler(val) {
        val; // To avoid warning
        if (!this.getDiagnoseFrequencyResponse) {
          return;
        }
        this.updateTable();
        this.updateColumns();
      }
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),
    ...mapActions('observation', ['usageCountUp']),

    /**
     * componentのmounted時に呼び出す処理
     */
    mountedLogic() {
      // データ読出し
      this.execute();
      // パスに"menu"を含まない場合はStart画面からの実行
      if (!this.$route.path.includes('menu')) {
        this.isExecutedFromStartScreen = true;
      }
    },

    /** 診断頻度読み出し */
    async execute() {
      try {
        this.processing = true;
        const systemNo = this.$route.params.systemNo;
        // イベントログの操作 ID 発行
        await eventLogUtil.createEventLogHandleId(this.netAppId);
        const response = await executePolling(
          this,
          // 診断頻度情報取得結果(要求)(POST API) 呼び出しのコールバック
          async () => {
            return await apiService.postCall(
              apiService.OBD,
              `/netApps/${this.netAppId}/obd/${this.obdDataId}/systems/${systemNo}/iumpr`,
            );
          },
          // 診断頻度情報取得状況の監視(GET API) 呼び出しのコールバック
          async (postApiResponse) => {
            return await apiService.getCall(
              apiService.OBD,
              `/netApps/${this.netAppId}/requests/${postApiResponse.data.request_id}`,
              this.operationScreen
            );
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API レスポンスチェックのコールバックはデフォルト処理を使用
          undefined,
          // GET API レスポンスチェックのコールバックはデフォルト処理を使用
          undefined,
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        // OBDシステムの利用回数カウントアップ
        this.usageCountUp({ type: OBD_DIAGNOSE_FREQUENCY });

        // responseが存在しない場合、処理終了
        if (!response) return;

        if (response.data.status === REQUEST_STATUS_COMPLETED) {
          // ポーリングが正常に完了した場合は、一覧表示を行う
          this.measureId = response.data.results.measure_id;
          this.getDiagnoseFrequencyResponse = await this.getDiagnoseFrequencyResult();
          // responseが存在しない場合、処理終了
          if (!this.getDiagnoseFrequencyResponse) return;

          this.updateTable();
          this.updateColumns();
        } else {
          // 異常終了 : 実行不可画面
          const messages = this.$t('fixed_resource.obd_function_common_message_result_ng_2');
          alert.showError(this, null,
            messages,
            () => {
              // 遷移処理
              pushFunctionList(this, this.$route.params.systemNo);
            });
        }
      } finally {
        this.processing = false;
      }
    },

    // 診断頻度情報取得結果
    async getDiagnoseFrequencyResult() {
      const response = await executeSyncRequest(
        this,
        async () => {
          return await apiService.getCall(
            apiService.OBD,
            `/obd/${this.obdDataId}/measures/${this.measureId}/iumpr`,
            this.operationScreen
          );
        },
        // エラー発生のコールバック
        (errorType) => { errorType; this.showUnexpectedError(); },
        // GET API で正常レスポンスが返ってきた際の内容チェックはデフォルト処理を利用
        undefined,
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する
        (result) => { this.validateErrorResult(result); },
      );

      // 処理失敗時は response が null になるので考慮
      return response ? response.data : null;
    },

    /**
     * テーブルヘッダーを作り直す
     */
    updateColumns() {
      if (!this.getDiagnoseFrequencyResponse) return;

      this.diagnoseFrequencyResultColumns = [
        { name: 'status', label: 'fixed_resource.header_vehicle_dtc_table_status' },
        { name: 'dtc', label: 'fixed_resource.header_vehicle_dtc_table_code', originalSortFunc: diagnoseResource.sortFuncCode },
        { name: 'description', label: 'fixed_resource.header_vehicle_dtc_table_description', useResource: true, originalSortFunc: diagnoseResource.sortFuncDescription },
        { name: 'molecule', label: 'fixed_resource.header_iumpr_molecule' },
        { name: 'denominator', label: 'fixed_resource.header_iumpr_denominator' },
        { name: 'mar', label: 'fixed_resource.header_iumpr_mar' },
      ];
    },

    /**
     * テーブル情報を更新する
     */
    async updateTable() {
      this.diagnoseFrequencyResultItems = createObdDiagnoseFrequencyItems(this.getDiagnoseFrequencyResponse.data, this);
    },

    /**
     * メモダイアログを表示する
     */
    openNoteModal() {
      this.modalMemo = true;
    },

    /**
     * メモダイアログを非表示にする
     */
    closeNoteModal() {
      this.modalMemo = false;
    },

    /**
     * メモ情報の保存
     */
    async memoSave() {
      await this.$refs.memo.save();
    },

    /**
     * メモ情報のクリア
     */
    memoClear: function () {
      this.$refs.memo.clear();
    },

    /**
     * その他のエラー発生時の処理を行う
     * @param {string} action 処理内容
     */
    showUnexpectedError() {
      const messages = this.$t('fixed_resource.obd_function_common_message_result_ng_2');
      alert.showError(this, null, messages,
        () => {
          // 遷移処理
          pushFunctionList(this, this.$route.params.systemNo);
        });
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    // 診断頻度情報をCSVエクスポートする
    async csvExport() {
      // 行のフォーマット
      const resultInfo = this.diagnoseFrequencyResultItems.map(item => {
        return {
          ...item,
          description: this.$t(item.description),
        };
      });
      // VINデータのフォーマット
      // Start画面から遷移した場合は、VIN取得をしないため、空文字で出力する。
      const vinData = this.isExecutedFromStartScreen ? '' : (this.vehicleInfo?.vehicle?.vin?.vin || '');
      const vinHeader = `"${this.$t('message.label_measured_data_search_vin')}","${vinData}"\n`;
      // 出力形式の設定
      const headers = this.diagnoseFrequencyResultColumns.map((column) => {
        return { id: column.name, title: this.$t(column.label) };
      });
      // 説明・故障部位のエスケープ処理
      const record = util.escapeDoubleQuotation(resultInfo, 'description');

      let endPoint = '';
      let measuredDataName = '';

      if (!this.isExecutedFromStartScreen && this.projectId !== null && this.measureId !== undefined) {
        try {
          this.processing = true;
          // メモボタン活性（パスに"menu"が存在）かつプロジェクトIDおよび計測データIDが存在する場合、計測データ取得APIを指定
          endPoint = '/projects/' + this.projectId + '/' + this.measureId;
          // 計測データ名を取得
          measuredDataName = await getMeasuredDataName(this, this.destinationAPI, endPoint, this.operationScreen);
        } finally {
          this.processing = false;
        }
      }
      const fileName = getCsvFileName(
        `${this.$t(this.functionNameKey)}${this.$t(this.obdFunctionLabelKey)}`,
        measuredDataName,
        this.isExecutedFromStartScreen ? '' : (this.vehicleInfo?.vehicle?.vin?.vin || '')
      );

      // CSVファイルエクスポート処理
      const data = vinHeader + util.createCsvData(headers, record);
      util.csvFileExport(data, fileName);
    },

    /**
     * 再取得ボタン押下時の処理を行う
     */
    async reGetDiagnoseFrequency() {
      const messages = this.$t('individual.message_diag_code_confirm_get_information');
      alert.showConfirmation(this, null,
        messages,
        () => {
          // 診断頻度の取得
          this.execute();
        });
    },
  },

  mounted() {
    this.mountedLogic();
  },
};
